import type { UserType } from '~/composables/onboarding/types';
import type { OnboardingConfig } from '~/onboarding/types';

const DAY = 24 * 3_600_000; // day in ms

type InitArgs = {
  postponedUntil?: Date;
  lastGuide?: string;
  lastStep?: number | null;
  guides: OnboardingConfig[];
  user: GuideUser;
};

type GuideUser = { userType: UserType; userId: string };

export type CurrentGuideState = {
  postponedUntil?: Date;
  guideId: string;
  stepIndex: number | null;
};

export const useOnboardingStore = defineStore('onboarding', () => {
  const currentGuide = ref('');
  const postponed = ref<Date | null>(null);
  const pending = ref(true);
  const step = ref<number | null>(null);
  const guides = ref<OnboardingConfig[]>([]);
  const user = ref<GuideUser | null>(null);
  const openType = ref<'manual' | 'auto'>('auto');

  function findFirstAutoPopupGuide() {
    return guides.value
      .filter(
        (config) =>
          !config.autoPopup ||
          !config.autoPopup.fromDate ||
          config.autoPopup.fromDate.getTime() <= Date.now() ||
          !config.autoPopup.toDate ||
          config.autoPopup.toDate.getDate() >= Date.now()
      )
      .at(0)?.id;
  }

  function init(args: InitArgs) {
    const { lastGuide, lastStep, postponedUntil, guides: guidesArg, user: userArg } = args;
    step.value = lastStep ?? null;
    guides.value = guidesArg;
    postponed.value = postponedUntil ? new Date(postponedUntil) : null;
    user.value = userArg;
    openType.value = 'auto';

    if (!postponedUntil || postponedUntil.getTime() < Date.now()) {
      currentGuide.value = lastGuide ?? '';
      if (!currentGuide.value) {
        const guideId = findFirstAutoPopupGuide();

        if (guideId) {
          currentGuide.value = guideId;
          step.value = null;
        }
      }
    }

    pending.value = false;
  }

  function finish() {
    if (!currentGuide.value) {
      return;
    }

    guides.value = guides.value.filter((guide) => guide.id !== currentGuide.value);
    currentGuide.value = findFirstAutoPopupGuide() ?? '';
  }

  function open(id: string, fromWelcome: boolean = false) {
    if (!guides.value.find((guide) => guide.id === id)) {
      return null;
    }
    if (fromWelcome) {
      step.value = null;
    }

    currentGuide.value = id;
  }

  function nextStep() {
    if (step.value === null) {
      step.value = 0;
    } else if (step.value < stepCount.value - 1) {
      step.value += 1;
    }

    postponed.value = null;
  }

  function previousStep() {
    if (step.value === null) {
      return;
    } else if (step.value === 0) {
      step.value = null;
    } else {
      step.value -= 1;
    }

    postponed.value = null;
  }

  function postpone() {
    postponed.value = new Date(Date.now() + DAY);
    const lastState = getCurrentState();
    currentGuide.value = '';

    return lastState;
  }

  function getCurrentState() {
    if (!currentGuide.value) {
      return null;
    }

    const state: CurrentGuideState = {
      guideId: currentGuide.value,
      stepIndex: step.value,
    };

    if (postponed.value) {
      state.postponedUntil = postponed.value;
    }

    return state;
  }

  const guideConfig = computed(() =>
    currentGuide.value ? guides.value.find((guide) => guide.id === currentGuide.value) : null
  );
  const currentStep = computed(() => {
    if (!guideConfig.value) {
      return null;
    }

    if (step.value === null) {
      return guideConfig.value.welcomeStep;
    }

    return guideConfig.value.steps[step.value] ?? null;
  });

  const stepCount = computed(() => guideConfig.value?.steps?.length ?? 0);

  function hasGuide(guideId: string) {
    return !!guides.value.find((guide) => guide.id === guideId);
  }

  function addGuide(guide: OnboardingConfig) {
    if (!hasGuide(guide.id)) {
      guides.value.push(guide);
    }
  }

  const currentGuideConfig = computed(() => guides.value.find((guide) => guide.id === currentGuide.value));

  return {
    init,
    finish,
    open,
    openType,
    nextStep,
    previousStep,
    postpone,
    getCurrentState,
    hasGuide,
    addGuide,
    pending: computed(() => pending.value),
    currentGuide: computed(() => currentGuide.value),
    currentStep,
    isWelcomeStep: computed(() => Boolean(currentGuide.value) && step.value === null),
    stepIndex: computed(() => step.value),
    stepCount,
    isLastStep: computed(() => Boolean(currentGuide.value) && step.value === stepCount.value - 1),
    user: computed(() => user.value),
    readTime: computed(() => currentGuideConfig.value?.readingTimeMinutes),
  };
});
